import React, { useEffect, useRef, useState } from 'react';
import WizardFrame from './WizardFrame';
import { INTEGRATIONS_LOGO_PATH } from '../common/constants';
import {
  getGoogleAccounts,
  refreshGoogleAccounts,
  saveGoogleMapping,
  disconnectGoogle,
  setExclusion,
  startOAuth
} from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// GoogleWizard
// ---------------------------------------------------------------- #
//
// The Google Analytics and Business Profile wizard (plan 07-google.md):
//   1. Connect: the connected Google account, or "Connect Google".
//   2. Accounts: lists the accounts again from Google and shows the counts.
//   3. Map parks: per park, one Analytics property and one Business
//      location, each with a "not needed" tick.
//   4. Review: the changes, and Save (the only step that writes).
//
// Props (the same contract as every provider wizard):
//   register       The `data` of GET register
//   parkiId        The park the wizard was opened from, or null
//   initialStep    The step to open at (2 after returning from Google)
//   connectionKey  Unused by Google (one connection per entity)
//   canConnect     False while staff are viewing as the customer
//   onClose        Called with true when something was saved, false otherwise

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The four step labels. */
const GOOGLE_STEPS = ['Connect', 'Accounts', 'Map parks', 'Review'];

/** Step numbers, named so the code reads plainly. */
const STEP_CONNECT = 1;
const STEP_ACCOUNTS = 2;
const STEP_MAP = 3;
const STEP_REVIEW = 4;

/** Message shown on the Connect button while staff are viewing as the customer. */
const IMPERSONATING_MESSAGE = 'Ask the customer to connect their own account';

/** Text of the empty choice in each select. */
const NO_CHOICE_LABEL = 'None';

// ---------------------------------------------------------------- #
// Helpers
// ---------------------------------------------------------------- #

/**
 * Show a database date-time ("YYYY-MM-DD HH:MM:SS") as a date only, day first ("03/11/2026"), or "" for null.
 */
const formatDate = (value) => {
  if (!value) {
    return '';
  }

  // Take the year, month and day from the start of the value
  const datePart = String(value).substring(0, 10);
  const pieces = datePart.split('-');
  const hasThreePieces = pieces.length === 3;
  if (!hasThreePieces) {
    return datePart;
  }

  // Put them back together day first
  const year = pieces[0];
  const month = pieces[1];
  const day = pieces[2];
  return `${day}/${month}/${year}`;
};

/**
 * Read each park's current Google choices from the register cells.
 */
const buildChoicesFromRegister = (register) => {
  // One entry per park, keyed by park id as a string
  const choices = {};
  for (const park of register?.parks || []) {
    const analyticsCell = park.cells?.analytics || {};
    const businessCell = park.cells?.business || {};

    choices[String(park.parkiId)] = {
      gaProperty: analyticsCell.mapping?.externalId || '',
      gbpLocation: businessCell.mapping?.externalId || '',
      analyticsNotNeeded: analyticsCell.notNeeded === true,
      businessNotNeeded: businessCell.notNeeded === true
    };
  }
  return choices;
};

/**
 * Group a sorted list of accounts by their accountName, keeping the order.
 */
const groupByAccount = (items) => {
  // Collect the groups in the order they first appear
  const groups = [];
  let currentGroup = null;
  for (const item of items) {
    const accountName = item.accountName || 'Other';
    const startsNewGroup = !currentGroup || currentGroup.accountName !== accountName;
    if (startsNewGroup) {
      currentGroup = { accountName: accountName, items: [] };
      groups.push(currentGroup);
    }
    currentGroup.items.push(item);
  }
  return groups;
};

/**
 * Find the display name for an id in a list of accounts, or describe it when missing.
 */
const describeChoice = (items, externalId) => {
  // An empty choice reads as None
  if (!externalId) {
    return NO_CHOICE_LABEL;
  }

  // Look the id up in the cached accounts
  for (const item of items) {
    if (String(item.externalId) === String(externalId)) {
      return item.name;
    }
  }

  // The id is mapped but no longer listed by Google
  return `${externalId} (no longer available)`;
};

/**
 * True when an id is empty or is one of the cached accounts. The save sends both fields of a park,
 * and the API refuses an id Google no longer lists, so these must be cleared or picked again first.
 */
const isChoiceAvailable = (items, externalId) => {
  // Nothing chosen is always fine
  if (!externalId) {
    return true;
  }

  // Look the id up in the cached accounts
  for (const item of items) {
    if (String(item.externalId) === String(externalId)) {
      return true;
    }
  }
  return false;
};

// ---------------------------------------------------------------- #
// The wizard
// ---------------------------------------------------------------- #

/**
 * The four-step Google Analytics and Business Profile wizard.
 */
const GoogleWizard = ({ register, parkiId = null, initialStep = 1, canConnect = true, onClose }) => {

  // ---------------------------------------------------------------- #
  // State
  // ---------------------------------------------------------------- #

  const [step, setStep] = useState(initialStep);
  const [maxReachedStep, setMaxReachedStep] = useState(initialStep);
  const [accounts, setAccounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshWarning, setRefreshWarning] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [changed, setChanged] = useState(false);
  const [savedChoices, setSavedChoices] = useState(() => buildChoicesFromRegister(register));
  const [choices, setChoices] = useState(() => buildChoicesFromRegister(register));
  const [problems, setProblems] = useState([]);
  const [saveSummary, setSaveSummary] = useState(null);
  const [parkFilter, setParkFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [activeParkKey, setActiveParkKey] = useState(null);

  // The opened park's list item, so the map step can scroll to it
  const openedParkRow = useRef(null);

  // Coming back from Google already listed the accounts, so step 2 does not list them again
  const autoRefreshDone = useRef(initialStep >= STEP_ACCOUNTS);

  // Read the cached lists, defaulting to empty
  const connection = accounts?.connection || null;
  const analyticsProperties = accounts?.analyticsProperties || [];
  const businessLocations = accounts?.businessLocations || [];
  const parks = register?.parks || [];

  // ---------------------------------------------------------------- #
  // Load the connection and cached accounts
  // ---------------------------------------------------------------- #

  // Read the Google connection when the wizard opens
  useEffect(() => {
    let cancelled = false;
    const loadAccounts = async () => {
      try {
        const result = await getGoogleAccounts();
        if (!cancelled) {
          setAccounts(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'The Google connection could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadAccounts();

    return () => {
      cancelled = true;
    };
  }, []);

  // ---------------------------------------------------------------- #
  // Step navigation
  // ---------------------------------------------------------------- #

  /**
   * Show a step and remember it as reached.
   */
  const goToStep = (stepNumber) => {
    setError(null);
    setNotice(null);
    setStep(stepNumber);
    if (stepNumber > maxReachedStep) {
      setMaxReachedStep(stepNumber);
    }
  };

  // Bring the opened park's list item into view when the map step opens
  useEffect(() => {
    const onMapStep = step === STEP_MAP;
    if (onMapStep && openedParkRow.current) {
      openedParkRow.current.scrollIntoView({ block: 'center' });
    }
  }, [step]);

  // ---------------------------------------------------------------- #
  // Step 1: connect and disconnect
  // ---------------------------------------------------------------- #

  /**
   * Send the browser to Google's sign-in.
   */
  const handleConnect = async () => {
    // Leaving the app loses unsaved choices, so ask first
    if (isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Ask the API for the sign-in address and go there
      const result = await startOAuth('google', parkiId);
      window.location.href = result.data.authoriseUrl;
    } catch (err) {
      setError(err.message || 'Google sign-in could not be started.');
      setBusy(false);
    }
  };

  /**
   * Disconnect Google for every park, after the user confirms.
   */
  const handleDisconnect = async () => {
    // Confirm, because every park loses its Google data feed
    const userAgreed = window.confirm('Disconnect Google? Every park stops receiving Google Analytics and Business Profile data until you connect again.');
    if (!userAgreed) {
      return;
    }

    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      // Disconnect, then read the (now empty) connection again
      const result = await disconnectGoogle();
      const accountsResult = await getGoogleAccounts();
      setAccounts(accountsResult.data);
      setChanged(true);

      // Tell the user whether Google itself was told; a login other Parki accounts use stays on at Google
      const otherAccountCount = result.data.sharedWithOtherEntities || 0;
      const loginIsShared = otherAccountCount > 0;
      if (loginIsShared) {
        setNotice(`Google disconnected from your parks. This Google login is also used by ${otherAccountCount} other Parki account(s), so access stays on at Google. Do not remove Parki at myaccount.google.com/permissions.`);
      } else if (result.data.revokedAtGoogle) {
        setNotice('Google disconnected.');
      } else {
        setNotice('Google disconnected in Parki. Google could not be reached, so also remove Parki at myaccount.google.com/permissions.');
      }
    } catch (err) {
      setError(err.message || 'Google could not be disconnected.');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- #
  // Step 2: list the accounts again
  // ---------------------------------------------------------------- #

  /**
   * Ask the API to list the Google accounts again.
   */
  const handleRefresh = async () => {
    setRefreshing(true);
    setError(null);
    setRefreshWarning(null);
    try {
      const result = await refreshGoogleAccounts();
      setAccounts(result.data);
      setRefreshWarning(result.data.warning || null);
    } catch (err) {
      setError(err.message || 'Google accounts could not be listed.');
    } finally {
      setRefreshing(false);
    }
  };

  // List the accounts once when step 2 is first reached (not after coming back from Google)
  useEffect(() => {
    const shouldAutoRefresh = step === STEP_ACCOUNTS && !loading && connection && !autoRefreshDone.current;
    if (!shouldAutoRefresh) {
      return;
    }
    autoRefreshDone.current = true;
    handleRefresh();
  });

  // ---------------------------------------------------------------- #
  // Step 3: choices
  // ---------------------------------------------------------------- #

  /**
   * Change one field of one park's choices.
   */
  const updateChoice = (parkKey, field, value) => {
    // A new edit means the last save result no longer describes the screen
    setSaveSummary(null);

    // Replace just this park's field
    setChoices(previous => {
      const parkChoices = { ...previous[parkKey], [field]: value };
      return { ...previous, [parkKey]: parkChoices };
    });
  };

  // ---------------------------------------------------------------- #
  // Step 4: changes and save
  // ---------------------------------------------------------------- #

  /**
   * List every change between the saved and the current choices, park by park.
   */
  const buildChanges = () => {
    const changes = [];
    for (const park of parks) {
      const parkKey = String(park.parkiId);
      const before = savedChoices[parkKey];
      const after = choices[parkKey];
      if (!before || !after) {
        continue;
      }

      // Compare each field
      const gaChanged = before.gaProperty !== after.gaProperty;
      const gbpChanged = before.gbpLocation !== after.gbpLocation;
      const analyticsTickChanged = before.analyticsNotNeeded !== after.analyticsNotNeeded;
      const businessTickChanged = before.businessNotNeeded !== after.businessNotNeeded;
      const anyChange = gaChanged || gbpChanged || analyticsTickChanged || businessTickChanged;
      if (!anyChange) {
        continue;
      }

      changes.push({
        park: park,
        before: before,
        after: after,
        mappingChanged: gaChanged || gbpChanged,
        gaChanged: gaChanged,
        gbpChanged: gbpChanged,
        analyticsTickChanged: analyticsTickChanged,
        businessTickChanged: businessTickChanged
      });
    }
    return changes;
  };

  /**
   * The parks whose save would send an Analytics property or Business location Google no longer lists,
   * with a line naming each one, so the Review step can ask for them to be cleared or picked again.
   */
  function findUnavailableChoices(changes) {
    const unavailable = [];
    for (const change of changes) {
      // Only parks whose mapping is being saved send their ids
      if (!change.mappingChanged) {
        continue;
      }

      // Check both ids the save will send
      const analyticsAvailable = isChoiceAvailable(analyticsProperties, change.after.gaProperty);
      const businessAvailable = isChoiceAvailable(businessLocations, change.after.gbpLocation);
      if (!analyticsAvailable) {
        unavailable.push(`${change.park.parkName}: Analytics property ${change.after.gaProperty} is no longer in your Google account. Pick another property or choose None.`);
      }
      if (!businessAvailable) {
        unavailable.push(`${change.park.parkName}: Business location ${change.after.gbpLocation} is no longer in your Google account. Pick another location or choose None.`);
      }
    }
    return unavailable;
  }

  /**
   * True when there are choices that have not been saved.
   */
  function isDirty() {
    return buildChanges().length > 0;
  }

  /**
   * Save the mapping changes, then the not-needed ticks.
   */
  const handleSave = async () => {
    const changes = buildChanges();

    // Build the save-mapping body: both fields for every park whose mapping changed
    const parksToSave = {};
    for (const change of changes) {
      if (!change.mappingChanged) {
        continue;
      }
      const parkKey = String(change.park.parkiId);
      parksToSave[parkKey] = {
        gaProperty: change.after.gaProperty || null,
        gbpLocation: change.after.gbpLocation || null
      };
    }
    const hasMappingChanges = Object.keys(parksToSave).length > 0;

    setBusy(true);
    setError(null);
    setProblems([]);
    try {
      // Save the property and location choices
      let statusChanges = [];
      if (hasMappingChanges) {
        const result = await saveGoogleMapping(parksToSave);
        statusChanges = result.data.statusChanges || [];
        setChanged(true);
      }

      // Save each not-needed tick that changed, one call per park and product
      let exclusionsSaved = 0;
      for (const change of changes) {
        if (change.analyticsTickChanged) {
          await setExclusion(change.park.parkiId, 'analytics', change.after.analyticsNotNeeded);
          exclusionsSaved += 1;
        }
        if (change.businessTickChanged) {
          await setExclusion(change.park.parkiId, 'business', change.after.businessNotNeeded);
          exclusionsSaved += 1;
        }
        setChanged(true);
      }

      // Count the parks the register would not let us change
      let skippedCount = 0;
      for (const statusChange of statusChanges) {
        if (statusChange.skippedBecause) {
          skippedCount += 1;
        }
      }

      // The current choices are now the saved ones
      setSavedChoices(choices);
      setSaveSummary({
        parksSaved: Object.keys(parksToSave).length,
        exclusionsSaved: exclusionsSaved,
        skippedCount: skippedCount
      });
    } catch (err) {
      setError(err.message || 'The changes could not be saved.');
      const returnedProblems = err.data?.problems;
      if (Array.isArray(returnedProblems)) {
        setProblems(returnedProblems);
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * Close the wizard, telling the page whether anything was saved.
   */
  const handleClose = () => {
    onClose(changed);
  };

  // ---------------------------------------------------------------- #
  // Render: step 1
  // ---------------------------------------------------------------- #

  /**
   * Draw the connected account, or the Connect button.
   */
  const renderConnectStep = () => {
    // Wait for the connection to load
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    // Staff viewing as the customer see a locked button; connecting is the customer's job
    let connectButton = (
      <button type="button" className="btn btn-primary" onClick={handleConnect} disabled={busy}>
        <i className="icon-base ti tabler-brand-google me-1"></i>
        {connection ? 'Reconnect Google' : 'Connect Google'}
      </button>
    );
    if (!canConnect) {
      connectButton = (
        <button type="button" className="btn btn-label-secondary" disabled title={IMPERSONATING_MESSAGE}>
          <i className="icon-base ti tabler-lock me-1"></i>
          Customer connects this
        </button>
      );
    }

    // The connected view: a green link badge, or a red broken link when Google refused the sign-in
    const hasConnectionError = Boolean(connection) && connection.status === 'error';
    let statusBadgeClass = 'integrations-status-badge is-connected';
    let statusIconClass = 'tabler-link';
    let statusHeading = 'Google is connected';
    if (hasConnectionError) {
      statusBadgeClass = 'integrations-status-badge is-error';
      statusIconClass = 'tabler-unlink';
      statusHeading = 'Google needs reconnecting';
    }

    // The line under the heading: the Google account when known, and the date it was connected
    let connectedLine = '';
    if (connection) {
      const connectedDate = formatDate(connection.connectedAt);
      connectedLine = `Connected since ${connectedDate}`;
      const accountName = (connection.externalUserName || '').trim();
      if (accountName !== '') {
        connectedLine = `${accountName} · connected since ${connectedDate}`;
      }
    }

    return (
      <>
        {notice && (
          <div className="alert alert-success" role="status">{notice}</div>
        )}

        {/* Not connected: one heading, one line, one button */}
        {!connection && (
          <div className="integrations-connect-hero">
            <h4>Connect Google</h4>
            <p>One sign-in covers Analytics and Business Profile for every park.</p>
            {connectButton}
          </div>
        )}

        {/* Connected: a green link badge, who signed in and since when, with Reconnect and Disconnect */}
        {connection && (
          <div className="integrations-connect-hero">
            <span className={statusBadgeClass} aria-hidden="true">
              <i className={`icon-base ti ${statusIconClass}`}></i>
            </span>
            <h4>{statusHeading}</h4>
            <p>{connectedLine}</p>
            {hasConnectionError && (
              <p className="text-danger">{connection.lastError || 'Google refused the saved sign-in. Please reconnect.'}</p>
            )}
            <div className="d-flex flex-wrap justify-content-center gap-2">
              {connectButton}
              <button type="button" className="btn btn-label-danger" onClick={handleDisconnect} disabled={busy}>
                Disconnect
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 2
  // ---------------------------------------------------------------- #

  /**
   * Draw the account counts and the Refresh button.
   */
  const renderAccountsStep = () => {
    // Show a spinner while Google is being asked
    if (refreshing || loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-2" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div>Asking Google for your accounts...</div>
        </div>
      );
    }

    // Read the counts the API worked out
    const propertyCount = accounts?.counts?.analyticsProperties ?? analyticsProperties.length;
    const locationCount = accounts?.counts?.businessLocations ?? businessLocations.length;

    return (
      <>
        <p className="fs-5">Found {propertyCount} Analytics {propertyCount === 1 ? 'property' : 'properties'} and {locationCount} Business {locationCount === 1 ? 'location' : 'locations'}.</p>

        {refreshWarning && (
          <div className="alert alert-warning" role="status">{refreshWarning}</div>
        )}

        {connection?.status === 'error' && connection.lastError && (
          <div className="alert alert-danger" role="status">{connection.lastError}</div>
        )}

        {connection?.lastFetchedAt && (
          <p className="text-body-secondary small">Last listed {formatDate(connection.lastFetchedAt)}.</p>
        )}

        <button type="button" className="btn btn-label-primary" onClick={handleRefresh} disabled={refreshing}>
          <i className="icon-base ti tabler-refresh me-1"></i>
          Refresh
        </button>
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 3, the park list and the park's choices
  // ---------------------------------------------------------------- #

  /**
   * The fields, lists and labels for one product, so Analytics and Business share the drawing code below.
   */
  const productSettings = (productKey) => {
    // Analytics properties by default
    let settings = {
      items: analyticsProperties,
      filterText: propertyFilter,
      setFilterText: setPropertyFilter,
      idField: 'gaProperty',
      tickField: 'analyticsNotNeeded',
      title: 'Analytics property',
      noneLabel: 'No Analytics property',
      itemWord: 'properties',
      iconClass: 'tabler-chart-bar',
      labelFor: (item) => item.name
    };

    // Business Profile locations
    if (productKey === 'business') {
      settings = {
        items: businessLocations,
        filterText: locationFilter,
        setFilterText: setLocationFilter,
        idField: 'gbpLocation',
        tickField: 'businessNotNeeded',
        title: 'Business location',
        noneLabel: 'No Business location',
        itemWord: 'locations',
        iconClass: 'tabler-map-pin',
        labelFor: (item) => item.name
      };
    }

    return settings;
  };

  /**
   * Work out where a park stands for one product: locked, not needed, chosen, missing or not chosen.
   */
  const describeProductState = (park, productKey) => {
    const parkKey = String(park.parkiId);
    const cell = park.cells?.[productKey] || {};
    const parkChoices = choices[parkKey] || {};
    const settings = productSettings(productKey);
    const chosenId = parkChoices[settings.idField] || '';
    let chosenAccount = null;
    for (const item of settings.items) {
      if (chosenId !== '' && String(item.externalId) === String(chosenId)) {
        chosenAccount = item;
      }
    }

    // The register will not let this cell change
    if (!cell.editable) {
      return { kind: 'locked', text: '' };
    }

    // Marked not needed
    if (parkChoices[settings.tickField] === true) {
      return { kind: 'not-needed', text: `${settings.title} not needed` };
    }

    // Nothing chosen
    if (chosenId === '') {
      return { kind: 'none', text: `no ${settings.title.toLowerCase()}` };
    }

    // Chosen, but Google no longer lists it
    if (!chosenAccount) {
      return { kind: 'missing', text: `${chosenId} (no longer available)` };
    }

    // Chosen and listed
    return { kind: 'chosen', text: settings.labelFor(chosenAccount) };
  };

  /**
   * The names of the other parks that use the same account as this one. A park marked not needed for
   * this product does not count, because it sends no data for it.
   */
  const listOtherParksUsing = (parkKey, idField, tickField, externalId) => {
    const parkNames = [];
    for (const park of parks) {
      const otherKey = String(park.parkiId);
      const isThisPark = otherKey === parkKey;
      if (isThisPark) {
        continue;
      }
      const otherChoices = choices[otherKey] || {};
      const otherIsNotNeeded = otherChoices[tickField] === true;
      if (otherIsNotNeeded) {
        continue;
      }
      const usesSameId = String(otherChoices[idField] || '') === String(externalId);
      if (usesSameId) {
        parkNames.push(park.parkName);
      }
    }
    return parkNames;
  };

  /**
   * Draw one park in the left-hand list: an icon for its state, its name and a one-line summary.
   */
  const renderParkListItem = (park, isActive) => {
    const analyticsState = describeProductState(park, 'analytics');
    const businessState = describeProductState(park, 'business');
    const isOpenedPark = parkiId !== null && String(park.parkiId) === String(parkiId);

    // The summary: what each product is set to, leaving out locked ones
    const summaryParts = [];
    if (analyticsState.kind !== 'locked') {
      summaryParts.push(analyticsState.text);
    }
    if (businessState.kind !== 'locked') {
      summaryParts.push(businessState.text);
    }
    const summary = summaryParts.join(' · ');

    // The icon: a warning when a choice is gone, a tick when both are settled, half when one is, dashed otherwise
    const states = [analyticsState.kind, businessState.kind];
    let settledCount = 0;
    let hasMissing = false;
    for (const kind of states) {
      const isSettled = kind === 'chosen' || kind === 'not-needed' || kind === 'locked';
      if (isSettled) {
        settledCount += 1;
      }
      if (kind === 'missing') {
        hasMissing = true;
      }
    }
    let iconClass = 'tabler-circle-dashed text-body-secondary';
    if (settledCount === 1) {
      iconClass = 'tabler-circle-half-2 text-warning';
    }
    if (settledCount === 2) {
      iconClass = 'tabler-circle-check text-success';
    }
    if (hasMissing) {
      iconClass = 'tabler-alert-triangle text-danger';
    }

    // The selected park is highlighted, and the opened park is scrolled into view
    let itemClass = 'integrations-mapper-item';
    if (isActive) {
      itemClass += ' is-active';
    }
    let itemRef = null;
    if (isOpenedPark) {
      itemRef = openedParkRow;
    }

    return (
      <button
        key={park.parkiId}
        ref={itemRef}
        type="button"
        className={itemClass}
        aria-current={isActive}
        onClick={() => setActiveParkKey(String(park.parkiId))}
      >
        <i className={`icon-base ti ${iconClass} integrations-mapper-item-icon`} aria-hidden="true"></i>
        <span className="integrations-mapper-item-text">
          <span className="integrations-mapper-item-name">{park.parkName}</span>
          {summary !== '' && (
            <span className="integrations-mapper-item-summary">{summary}</span>
          )}
        </span>
      </button>
    );
  };

  /**
   * Draw one product's section of the panel: its heading and "Not needed" switch, then a short searchable list to pick from.
   */
  const renderChoiceSection = (park, productKey) => {
    const parkKey = String(park.parkiId);
    const cell = park.cells?.[productKey] || {};
    const parkChoices = choices[parkKey] || {};
    const settings = productSettings(productKey);
    const chosenId = String(parkChoices[settings.idField] || '');
    const isNotNeeded = parkChoices[settings.tickField] === true;
    const switchId = `google-${productKey}-not-needed-${parkKey}`;
    const radioName = `google-${productKey}-${parkKey}`;

    // The register will not let this cell change: say why
    if (!cell.editable) {
      let lockedLabel = 'Not in plan';
      if (cell.state === 'contact-support') {
        lockedLabel = 'Contact support';
      }
      return (
        <div className="integrations-choice-section">
          <div className="integrations-mapper-panel-head">
            <span className="integrations-mapper-label">
              <i className={`icon-base ti ${settings.iconClass} me-1`} aria-hidden="true"></i>
              {settings.title}
            </span>
          </div>
          <p className="text-body-secondary small mb-0">{lockedLabel}. {cell.reason || ''}</p>
        </div>
      );
    }

    // Keep the accounts matching the filter, plus the current choice
    const filter = settings.filterText.trim().toLowerCase();
    const visibleItems = [];
    let chosenIsListed = false;
    for (const item of settings.items) {
      const isChosen = String(item.externalId) === chosenId;
      if (isChosen) {
        chosenIsListed = true;
      }
      const searchable = `${item.name} ${item.accountName || ''}`.toLowerCase();
      const matchesFilter = filter === '' || searchable.includes(filter);
      if (!matchesFilter && !isChosen) {
        continue;
      }
      visibleItems.push(item);
    }

    // Group them by Google account, shown as small headings when there is more than one
    const accountGroups = groupByAccount(visibleItems);
    const showGroupLabels = accountGroups.length > 1;

    // A mapped id that Google no longer lists still needs a row to show
    const showMissingRow = chosenId !== '' && !chosenIsListed;

    /**
     * Draw one choice row: a radio, the account's name and a note on which other park uses it. A row another park holds is disabled.
     */
    const renderChoiceRow = (value, label, usage) => {
      const isOn = chosenId === value;
      let rowClass = 'integrations-choice-row';
      if (isOn) {
        rowClass += ' is-on';
      }
      if (usage.isTaken) {
        rowClass += ' is-taken';
      }
      let noteClass = 'integrations-choice-note';
      if (usage.isWarning) {
        noteClass += ' is-warning';
      }
      return (
        <label key={value || 'none'} className={rowClass}>
          <input
            type="radio"
            className="form-check-input"
            name={radioName}
            checked={isOn}
            disabled={usage.isTaken}
            onChange={() => updateChoice(parkKey, settings.idField, value)}
          />
          <span className="integrations-choice-name">{label}</span>
          {usage.note !== '' && (
            <span className={noteClass}>{usage.note}</span>
          )}
        </label>
      );
    };

    /**
     * How an account is used elsewhere. Each account belongs to one park only, so a report never counts the
     * same page, profile or property twice: one another park holds is locked here, and if this park already
     * shares it (set up before this rule), the note warns so it can be fixed.
     */
    const describeUsage = (externalId) => {
      const otherParkNames = listOtherParksUsing(parkKey, settings.idField, settings.tickField, externalId);
      const otherCount = otherParkNames.length;
      const isChosenHere = chosenId === String(externalId);

      // Nobody else uses it: free to pick
      if (otherCount === 0) {
        return { note: '', isTaken: false, isWarning: false };
      }

      // Name the first other park, and count the rest
      let usedByText = otherParkNames[0];
      if (otherCount > 1) {
        usedByText = `${otherParkNames[0]} and ${otherCount - 1} more`;
      }

      // This park shares it with others already: keep it pickable so it can be let go, and warn
      if (isChosenHere) {
        return { note: `Also used by ${usedByText}`, isTaken: false, isWarning: true };
      }

      // Another park holds it: locked here until that park lets it go
      return { note: `Used by ${usedByText}`, isTaken: true, isWarning: false };
    };

    /** The usage of a row that is not an account (None, or an account no longer listed). */
    const noUsage = { note: '', isTaken: false, isWarning: false };

    return (
      <div className="integrations-choice-section">
        {/* Heading with the not-needed switch */}
        <div className="integrations-mapper-panel-head">
          <span className="integrations-mapper-label">
            <i className={`icon-base ti ${settings.iconClass} me-1`} aria-hidden="true"></i>
            {settings.title}
          </span>
          <div className="form-check form-switch mb-0">
            <input
              id={switchId}
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={isNotNeeded}
              onChange={(event) => updateChoice(parkKey, settings.tickField, event.target.checked)}
            />
            <label className="form-check-label" htmlFor={switchId}>Not needed</label>
          </div>
        </div>

        {/* Not needed: one line instead of the list */}
        {isNotNeeded && (
          <p className="text-body-secondary small mb-0">This park does not need a {settings.title.toLowerCase()}.</p>
        )}

        {/* The list to pick from, with its own filter */}
        {!isNotNeeded && (
          <div className="integrations-choice-list">
            <div className="integrations-choice-filter">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder={`Filter ${settings.items.length} ${settings.itemWord}`}
                aria-label={`Filter ${settings.title.toLowerCase()}s`}
                value={settings.filterText}
                onChange={(event) => settings.setFilterText(event.target.value)}
              />
            </div>
            <div className="integrations-choice-rows" role="radiogroup" aria-label={settings.title}>
              {showMissingRow && renderChoiceRow(chosenId, `${chosenId} (no longer available)`, { note: 'Pick another', isTaken: false, isWarning: false })}
              {accountGroups.map(group => (
                <React.Fragment key={group.accountName}>
                  {showGroupLabels && (
                    <div className="integrations-choice-group">{group.accountName}</div>
                  )}
                  {group.items.map(item => renderChoiceRow(String(item.externalId), item.name, describeUsage(item.externalId)))}
                </React.Fragment>
              ))}
              {visibleItems.length === 0 && (
                <div className="integrations-choice-empty">Nothing matches "{settings.filterText}".</div>
              )}
              {renderChoiceRow('', settings.noneLabel, noUsage)}
            </div>
          </div>
        )}
      </div>
    );
  };

  /**
   * Draw the Map parks step: the park list on the left and the selected park's Analytics and Business choices on the right.
   */
  const renderMapStep = () => {
    // Keep the parks whose name matches the search, split into mapped and not mapped
    const parkFilterText = parkFilter.trim().toLowerCase();
    const mappedParks = [];
    const unmappedParks = [];
    for (const park of parks) {
      const parkName = (park.parkName || '').toLowerCase();
      const matches = parkFilterText === '' || parkName.includes(parkFilterText);
      if (!matches) {
        continue;
      }
      const parkChoices = choices[String(park.parkiId)] || {};
      const hasAnyChoice = Boolean(parkChoices.gaProperty) || Boolean(parkChoices.gbpLocation);
      const bothNotNeeded = parkChoices.analyticsNotNeeded === true && parkChoices.businessNotNeeded === true;
      if (hasAnyChoice || bothNotNeeded) {
        mappedParks.push(park);
      } else {
        unmappedParks.push(park);
      }
    }

    // The selected park: the one clicked, else the opened park, else the first park
    let activePark = null;
    for (const park of parks) {
      if (String(park.parkiId) === activeParkKey) {
        activePark = park;
      }
    }
    if (!activePark) {
      for (const park of parks) {
        const isOpenedPark = parkiId !== null && String(park.parkiId) === String(parkiId);
        if (isOpenedPark) {
          activePark = park;
        }
      }
    }
    if (!activePark && parks.length > 0) {
      activePark = parks[0];
    }
    let activeKey = null;
    if (activePark) {
      activeKey = String(activePark.parkiId);
    }

    return (
      <div className="integrations-mapper">
        {/* Left: search, then the mapped parks, then the rest */}
        <nav className="integrations-mapper-list" aria-label="Parks">
          <div className="integrations-mapper-search">
            <input
              type="search"
              className="form-control form-control-sm"
              placeholder="Search parks"
              aria-label="Search parks"
              value={parkFilter}
              onChange={(event) => setParkFilter(event.target.value)}
            />
          </div>
          <div className="integrations-mapper-items">
            <div className="integrations-mapper-group">Mapped ({mappedParks.length})</div>
            {mappedParks.map(park => renderParkListItem(park, String(park.parkiId) === activeKey))}
            <div className="integrations-mapper-group">Not mapped ({unmappedParks.length})</div>
            {unmappedParks.map(park => renderParkListItem(park, String(park.parkiId) === activeKey))}
          </div>
        </nav>

        {/* Right: the selected park's choices */}
        {activePark && (
          <div className="integrations-mapper-panel">
            <h5 className="mb-0">{activePark.parkName}</h5>
            {renderChoiceSection(activePark, 'analytics')}
            {renderChoiceSection(activePark, 'business')}
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 4
  // ---------------------------------------------------------------- #

  /**
   * Draw the list of changes, and the result once saved.
   */
  const renderReviewStep = () => {
    // After saving, say what happened
    if (saveSummary) {
      return (
        <>
          <div className="alert alert-success" role="status">Saved.</div>
          {saveSummary.skippedCount > 0 && (
            <p className="text-body-secondary">{saveSummary.skippedCount} park(s) could not be changed because they are not in your plan or need Parki support.</p>
          )}
        </>
      );
    }

    // List the changes park by park
    const changes = buildChanges();
    if (changes.length === 0) {
      return <p className="mb-0">No changes to save.</p>;
    }
    const unavailableChoices = findUnavailableChoices(changes);

    return (
      <>
        <p>These changes will be saved:</p>
        <ul className="integrations-review-list">
          {changes.map(change => (
            <li key={change.park.parkiId}>
              <strong>{change.park.parkName}</strong>
              <ul>
                {change.gaChanged && (
                  <li>Analytics property: {describeChoice(analyticsProperties, change.before.gaProperty)} to {describeChoice(analyticsProperties, change.after.gaProperty)}</li>
                )}
                {change.gbpChanged && (
                  <li>Business location: {describeChoice(businessLocations, change.before.gbpLocation)} to {describeChoice(businessLocations, change.after.gbpLocation)}</li>
                )}
                {change.analyticsTickChanged && (
                  <li>Analytics: {change.after.analyticsNotNeeded ? 'marked not needed' : 'needed again'}</li>
                )}
                {change.businessTickChanged && (
                  <li>Business: {change.after.businessNotNeeded ? 'marked not needed' : 'needed again'}</li>
                )}
              </ul>
            </li>
          ))}
        </ul>

        {/* Mapped ids Google no longer lists; Save stays off until they are cleared or picked again */}
        {unavailableChoices.length > 0 && (
          <div className="alert alert-warning" role="alert">
            <p>Go back to step 3 and fix these before saving:</p>
            <ul className="mb-0">
              {unavailableChoices.map((line, index) => (
                <li key={index}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Rule problems returned by the API */}
        {problems.length > 0 && (
          <div className="alert alert-danger" role="alert">
            <ul className="mb-0">
              {problems.map((problem, index) => (
                <li key={index}>{problem.message}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Footer button for each step
  // ---------------------------------------------------------------- #

  // Next by default; Save on Review; Close once saved
  let primaryLabel = 'Next';
  let onPrimary = () => goToStep(step + 1);
  let primaryDisabled = false;
  if (step === STEP_CONNECT) {
    primaryDisabled = !connection;
  } else if (step === STEP_ACCOUNTS) {
    primaryDisabled = refreshing || !connection;
  } else if (step === STEP_REVIEW) {
    if (saveSummary) {
      primaryLabel = 'Close';
      onPrimary = handleClose;
    } else {
      primaryLabel = 'Save';
      onPrimary = handleSave;
      const reviewChanges = buildChanges();
      const hasNoChanges = reviewChanges.length === 0;
      const hasUnavailableChoices = findUnavailableChoices(reviewChanges).length > 0;
      primaryDisabled = hasNoChanges || hasUnavailableChoices;
    }
  }

  /**
   * Keep step 3's ticks in step with a not-needed switch saved from the footer.
   */
  function handleNotNeededChange(productKey, notNeeded) {
    // Which tick the product maps to
    let choiceKey = 'businessNotNeeded';
    if (productKey === 'analytics') {
      choiceKey = 'analyticsNotNeeded';
    }

    // Saved and current choices both take the new value, so it is not counted as an unsaved change
    const parkKey = String(parkiId);
    setSavedChoices(previous => ({ ...previous, [parkKey]: { ...previous[parkKey], [choiceKey]: notNeeded } }));
    setChoices(previous => ({ ...previous, [parkKey]: { ...previous[parkKey], [choiceKey]: notNeeded } }));
    setChanged(true);
  }

  // Unsaved choices make closing ask first
  const dirty = isDirty();

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <WizardFrame
      title="Google Analytics and Business Profile"
      logoSrc={`${INTEGRATIONS_LOGO_PATH}/google-analytics.png`}
      register={register}
      parkiId={parkiId}
      products={['analytics', 'business']}
      onNotNeededChange={handleNotNeededChange}
      steps={GOOGLE_STEPS}
      step={step}
      maxReachedStep={maxReachedStep}
      onStepChange={goToStep}
      error={error}
      dirty={dirty}
      onClose={handleClose}
      primaryLabel={primaryLabel}
      onPrimary={onPrimary}
      primaryDisabled={primaryDisabled}
      busy={busy}
      hideBack={Boolean(saveSummary)}
    >
      {step === STEP_CONNECT && renderConnectStep()}
      {step === STEP_ACCOUNTS && renderAccountsStep()}
      {step === STEP_MAP && renderMapStep()}
      {step === STEP_REVIEW && renderReviewStep()}
    </WizardFrame>
  );
};

export default GoogleWizard;
